import React, { FC, ReactNode, useState } from 'react';
import { TextButton } from '../../../../widgets/buttons';
import { stringFormat, T } from 'src/shared/helpers';
import styled from 'styled-components';
import SpaceDashboardIcon from 'src/shared/material_icons/action/space_dashboard.svg';
import GridOnIcon from 'src/shared/material_icons/image/grid_on.svg';
import LayersIcon from 'src/shared/material_icons/maps/layers.svg';
import TouchAppIcon from 'src/shared/material_icons/action/touch_app.svg';
import ViewInArIcon from 'src/shared/material_icons/action/view_in_ar.svg';
import { ObjectsIcon, VtoIcon } from '../../../../widgets/icons/scene';
import AREditor from './scene-editors/arEditor';
import SettingsEditor from './scene-editors/settingsEditor';
import VTOEditor from './scene-editors/vtoEditor';
import { WorkspacePreferenceKey, WorkspacePreferences } from '../configurator-scene-editor/useWorkspacePreferences';
import { SelectionHighlight } from '../configurator-scene-editor/selectionHighlight';
import { PropertiesPanelColorControl } from './scene-properties-panel/propertiesPanelColorProperty';
import { PropertiesPanelSegmented } from './scene-properties-panel/propertiesPanelSegmentedProperty';
import { SettingControl, SettingsFieldLabel, SettingsFieldRow, SettingsGroup, SettingsPage, SettingsToggleRow } from './sceneEditorSettingsLayout';
import { SHOW_NOT_YET_AVAILABLE } from './scene-editors/notYetAvailable';
import { SceneSettingsPage } from './scene-editors/sceneSettingsRequest';
import { editorPalette } from './editorPalette';
import { ZTooltipLookContext } from '../../../../widgets/ztooltip';
import { editorDialogWindow } from './editorDialogWindow';

type SettingsCategory = SceneSettingsPage;

// CHI APRE LA FINESTRA DECIDE COSA SI VEDE (utente, 2026-10-19). Scene, Rendering, Behaviors, AR e VTO parlano
// della scena 3D e hanno senso solo nello Scene Editor; Logics — il generatore AI — solo nel Composer.
export type SettingsHost = 'sceneEditor' | 'composer';

interface SceneEditorSettingsDialogProps {
    host: SettingsHost;
    /** La pagina da cui si parte: chi apre da un ingranaggio sa già dove vuole arrivare. */
    initialCategory?: SettingsCategory;
    preferences: WorkspacePreferences;
    setPreference: <K extends WorkspacePreferenceKey>(key: K, value: WorkspacePreferences[K]) => void;
    /** Lo passa solo lo Scene Editor, che tiene l'alone: senza, la sezione Viewport non c'è. */
    selectionHighlight?: SelectionHighlight;
    onSelectionHighlightChange?: (changes: Partial<SelectionHighlight>) => void;
    /**
     * Rimette le colonne alle misure di fabbrica. Non è fra le scelte qui sopra perché non è una
     * scelta: è un rimedio, e come tale non ha uno stato da mostrare — si preme e basta.
     */
    onResetPanelSizes?: () => void;
    sceneEditor: any;
    zkScene: any;
    zkProduct: any;
    additionalFeatures?: any;
    /**
     * QUI DENTRO SI TOCCA LA SCENA, e la scena va segnata da salvare.
     *
     * Chi tiene il pennarello ascolta gli eventi dentro l'area di lavoro, ma questa finestra si
     * disegna in un portale attaccato alla radice della pagina: quello che si cambia qui non
     * passa mai di lì. Senza questa richiamata, spegnere l'AR cambiava davvero la
     * configurazione e poi «Save» rispondeva «nulla da salvare» — il valore restava vivo fino
     * al ricaricamento e non arrivava mai al server.
     */
    onSceneChanged?: () => void;
}

// Non disegna: ascolta. `display: contents` lo toglie dal disegno, così le pagine restano
// figlie di `Content` e nessun layout si accorge che è passato di qui.
const SceneChanges = styled.div`
    display: contents;
`;

export const SettingsDialogWindow = editorDialogWindow(720, 560, false, true);

// DENTRO, IL PROTOTIPO: a sinistra il menu sottile sul tono dei pannelli, a destra le pagine sul fondo dell'Inspector,
// dove le card si staccano. La finestra sta in un portale fuori dagli editor, e i loro colori se li porta dietro da qui.
const DialogLayout = styled.div`
    ${editorPalette}

    display: grid;
    grid-template-columns: 184px minmax(0, 1fr);
    height: 100%;
    overflow: hidden;
`;

const CategoryList = styled.nav`
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 8px;
    background: var(--editor-panel);
    border-right: 1px solid var(--editor-line-soft);
    overflow-y: auto;
`;

const ItemButton = styled.button<{ $active?: boolean }>`
    display: flex;
    align-items: center;
    gap: 8px;
    height: 30px;
    padding: 0 8px;
    border: 0;
    border-radius: 8px;
    /* LA VOCE APERTA HA UN SUO GRIGIO (prototipo, --veil): diverso da quello del passaggio, si capisce dove si è. */
    background: ${(props) => (props.$active ? 'rgb(0 0 0 / 6%)' : 'transparent')};
    color: ${(props) => (props.$active ? 'var(--editor-fg)' : 'var(--editor-muted)')};
    font-family: inherit;
    font-size: 13px;
    font-weight: ${(props) => (props.$active ? 500 : 400)};
    text-align: left;
    cursor: pointer;
    transition:
        background 120ms ease,
        color 120ms ease;

    svg {
        flex: none;
        width: 16px;
        height: 16px;
        fill: currentColor;
        color: ${(props) => (props.$active ? 'var(--editor-accent-text)' : 'currentColor')};
    }

    &:hover,
    &:focus-visible {
        background: ${(props) => (props.$active ? 'rgb(0 0 0 / 6%)' : 'var(--editor-hover)')};
        color: var(--editor-fg);
    }
`;

const Content = styled.div`
    min-width: 0;
    background: var(--editor-sunken);
    overflow-x: hidden;
    overflow-y: auto;
`;

// OGNI SCELTA DELL'INTERFACCIA È UN PICCOLO DISEGNO DELL'EDITOR (prototipo, `.tile-pick`): la colonna delle sezioni,
// l'elenco, il palco e le proprietà, nell'ordine in cui la scelta li mette.
const Tiles = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
`;

const Tile = styled.button`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    padding: 0;
    border: 0;
    background: none;
    color: var(--editor-muted);
    font-family: inherit;
    font-size: 12px;
    cursor: pointer;

    &[aria-checked='true'] {
        color: var(--editor-fg);
        font-weight: 500;
    }

    &:focus-visible {
        outline: none;
    }
`;

const TilePreview = styled.span`
    display: flex;
    gap: 2px;
    width: 100%;
    height: 76px;
    padding: 6px;
    border-radius: 8px;
    background: var(--editor-sunken);
    box-shadow: inset 0 0 0 1px var(--editor-line);
    transition: box-shadow 120ms ease;

    ${Tile}:hover & {
        box-shadow: inset 0 0 0 1px var(--editor-faint);
    }

    ${Tile}[aria-checked='true'] & {
        box-shadow: 0 0 0 2px var(--editor-accent);
    }

    ${Tile}:focus-visible & {
        box-shadow: 0 0 0 2px var(--editor-accent);
    }
`;

type TilePart = 'rail' | 'list' | 'stage' | 'panel';

// La colonna che la scelta sposta è arancio pieno (prototipo, all'80%); ogni blocco ha il suo filo.
const TileBar = styled.i<{ $part: TilePart; $on?: boolean }>`
    display: block;
    border-radius: 4px;
    background: ${(props) => (props.$on ? 'rgb(232 101 10 / 80%)' : 'var(--editor-raised)')};
    box-shadow: inset 0 0 0 1px var(--editor-line);
    width: ${(props) => ({ rail: '10%', list: '22%', stage: 'auto', panel: '24%' })[props.$part]};
    flex: ${(props) => (props.$part === 'stage' ? 1 : 'none')};
    opacity: ${(props) => (props.$part === 'stage' ? 0.5 : 1)};
`;

const TilePick: FC<{ label: string; options: { value: string; label: string; parts: TilePart[] }[]; value: string; onChange: (value: string) => void }> = ({
    label,
    options,
    value,
    onChange
}) => (
    <Tiles role='radiogroup' aria-label={label}>
        {options.map((option) => (
            <Tile key={option.value} type='button' role='radio' aria-checked={option.value === value} onClick={() => onChange(option.value)}>
                <TilePreview aria-hidden='true'>
                    {option.parts.map((part) => (
                        <TileBar key={part} $part={part} $on={part === 'panel'} />
                    ))}
                </TilePreview>
                {option.label}
            </Tile>
        ))}
    </Tiles>
);

const CATEGORIES: { key: SettingsCategory; icon: ReactNode; title: string; hosts: SettingsHost[] }[] = [
    { key: 'interface', icon: <SpaceDashboardIcon />, title: T._('Interface', 'Admin'), hosts: ['sceneEditor', 'composer'] },
    { key: 'scene', icon: <ObjectsIcon />, title: T._('Scene', 'Admin'), hosts: ['sceneEditor'] },
    { key: 'logics', icon: <LayersIcon />, title: T._('Logics', 'Admin'), hosts: ['composer'] },
    { key: 'rendering', icon: <GridOnIcon />, title: T._('Rendering', 'Admin'), hosts: ['sceneEditor'] },
    { key: 'behaviours', icon: <TouchAppIcon />, title: T._('Behaviors', 'Admin'), hosts: ['sceneEditor'] },
    { key: 'ar', icon: <ViewInArIcon />, title: T._('Augmented Reality', 'Admin'), hosts: ['sceneEditor'] },
    { key: 'vto', icon: <VtoIcon />, title: T._('Virtual Try-On', 'Admin'), hosts: ['sceneEditor'] }
];

const SceneEditorSettingsDialog: FC<SceneEditorSettingsDialogProps> = ({
    host,
    preferences,
    setPreference,
    selectionHighlight,
    onSelectionHighlightChange,
    onResetPanelSizes,
    sceneEditor,
    zkScene,
    zkProduct,
    additionalFeatures,
    onSceneChanged,
    initialCategory
}) => {
    const categories = CATEGORIES.filter((item) => item.hosts.includes(host));
    const [category, setCategory] = useState<SettingsCategory>(
        initialCategory && categories.some((item) => item.key === initialCategory) ? initialCategory : 'interface'
    );
    // Il gestore dei dialoghi conserva il nodo così com'è stato creato: le props non tornano più. Lo stato
    // corrente delle scelte sta quindi qui, e ogni scelta viene comunque applicata subito fuori.
    const [values, setValues] = useState(preferences);

    const choose = <K extends WorkspacePreferenceKey>(key: K, value: WorkspacePreferences[K]) => {
        setValues((current) => ({ ...current, [key]: value }));
        setPreference(key, value);
    };

    const [highlight, setHighlight] = useState(selectionHighlight);

    const changeHighlight = (changes: Partial<SelectionHighlight>) => {
        setHighlight((current) => current && { ...current, ...changes });
        onSelectionHighlightChange?.(changes);
    };

    const savedWithProduct = T._('Saved with the product when you click Save.', 'Admin');

    // L'INTERFACCIA È DI CHI GUARDA, NON DEL PRODOTTO: si salva subito, per chi la sceglie. Il colore dell'alone
    // sta qui perché è una preferenza come le altre, e ha senso solo ad alone acceso.
    const renderInterface = () => (
        <SettingsPage title={T._('Interface', 'Admin')} lead={T._('How the editor looks on this computer. Saved for you, right away.', 'Admin')}>
            {/* IL TEMA NON SI APPLICA ANCORA: nessuna delle due stanze ha una veste scura. Resta scritto e spento
                finché non c'è — vedi `SHOW_NOT_YET_AVAILABLE`. */}
            {SHOW_NOT_YET_AVAILABLE && (
                <SettingsGroup title={T._('Theme', 'Admin')}>
                    <TilePick
                        label={T._('Interface theme', 'Admin')}
                        value={values.colorScheme}
                        onChange={(value) => choose('colorScheme', value as WorkspacePreferences['colorScheme'])}
                        options={[
                            { value: 'light', label: T._('Light', 'Admin'), parts: ['rail', 'list', 'stage', 'panel'] },
                            { value: 'dark', label: T._('Dark', 'Admin'), parts: ['rail', 'list', 'stage', 'panel'] },
                            { value: 'system', label: T._('System', 'Admin'), parts: ['rail', 'list', 'stage', 'panel'] }
                        ]}
                    />
                </SettingsGroup>
            )}
            {/* LA COLONNA DELLE PROPRIETÀ VALE PER TUTTI E DUE (utente, 2026-10-14): anche il Composer ha il suo
                Inspector accanto al 3D, e la stessa preferenza lo mette accanto all'albero o a destra. */}
            <SettingsGroup title={T._('Properties panel', 'Admin')} bare>
                <TilePick
                    label={T._('Properties panel position', 'Admin')}
                    value={values.propertiesPanel}
                    onChange={(value) => choose('propertiesPanel', value as WorkspacePreferences['propertiesPanel'])}
                    options={[
                        { value: 'side', label: T._('Left', 'Admin'), parts: ['rail', 'list', 'panel', 'stage'] },
                        { value: 'right', label: T._('Right', 'Admin'), parts: ['rail', 'list', 'stage', 'panel'] }
                    ]}
                />
            </SettingsGroup>
            <SettingsGroup title={T._('Layout', 'Admin')}>
                <SettingsFieldRow>
                    <SettingsFieldLabel as='span'>{T._('Section rails', 'Admin')}</SettingsFieldLabel>
                    <PropertiesPanelSegmented
                        ariaLabel={T._('Section rails', 'Admin')}
                        value={values.sectionRails}
                        onChange={(value) => choose('sectionRails', value as WorkspacePreferences['sectionRails'])}
                        options={[
                            { value: 'compact', label: T._('Icons only', 'Admin') },
                            { value: 'expanded', label: T._('Icons and names', 'Admin') }
                        ]}
                    />
                </SettingsFieldRow>
                {onResetPanelSizes && (
                    <SettingsFieldRow>
                        <SettingsFieldLabel as='span'>{T._('Panel sizes', 'Admin')}</SettingsFieldLabel>
                        <TextButton onClick={onResetPanelSizes}>{T._('Reset to default', 'Admin')}</TextButton>
                    </SettingsFieldRow>
                )}
            </SettingsGroup>
            {highlight && (
                <SettingsGroup title={T._('Viewport', 'Admin')}>
                    <SettingsToggleRow
                        id='settings-selection-highlight'
                        label={T._('Selection highlight', 'Admin')}
                        sub={T._('An outline around what you select on the stage', 'Admin')}
                        checked={highlight.enabled}
                        onChange={(enabled) => changeHighlight({ enabled })}
                    />
                    <SettingsFieldRow>
                        <SettingsFieldLabel htmlFor='settings-highlight-color'>{T._('Highlight color', 'Admin')}</SettingsFieldLabel>
                        <SettingControl>
                            <PropertiesPanelColorControl
                                id='settings-highlight-color'
                                value={highlight.color}
                                disabled={!highlight.enabled}
                                onChange={(color) => changeHighlight({ color })}
                            />
                        </SettingControl>
                    </SettingsFieldRow>
                </SettingsGroup>
            )}
        </SettingsPage>
    );

    const sceneSettings = (section: 'scene' | 'rendering' | 'behaviours' | 'logics') => (
        <SettingsEditor
            section={section}
            sceneEditor={sceneEditor}
            zkScene={zkScene}
            zkProduct={zkProduct}
            isAIEnabled={zkProduct?.get?.('isAiConfigurationEnabled')}
            setAIEnabled={(enabled) => zkProduct?.set?.('isAiConfigurationEnabled', enabled)}
            additionalFeatures={additionalFeatures}
            onSceneChanged={onSceneChanged}
        />
    );

    // LA FINESTRA STA IN UN PORTALE, FUORI DAGLI EDITOR: il tooltip scuro degli editor glielo si ridà da qui.
    return (
        <ZTooltipLookContext.Provider value='editor'>
            <DialogLayout>
                <CategoryList aria-label={T._('Settings groups', 'Admin')}>
                    {categories.map((item) => (
                        <ItemButton
                            key={item.key}
                            type='button'
                            $active={category === item.key}
                            aria-current={category === item.key ? 'page' : undefined}
                            onClick={() => setCategory(item.key)}
                        >
                            {item.icon}
                            {item.title}
                        </ItemButton>
                    ))}
                </CategoryList>
                {/* CAMBIARE GRUPPO È CAMBIARE PAGINA: si riparte dall'alto. */}
                <Content key={category}>
                    {/* L’INTERFACCIA STA FUORI DALL’ASCOLTO, e non per dimenticanza: tema e disposizione
                    dei pannelli sono preferenze di chi guarda, non dati della scena. Segnarle da
                    salvare accenderebbe «modifiche non salvate» per aver cambiato colore allo sfondo. */}
                    {category === 'interface' && renderInterface()}
                    <SceneChanges onChangeCapture={onSceneChanged} onInputCapture={onSceneChanged}>
                        {category === 'scene' && sceneSettings('scene')}
                        {category === 'rendering' && sceneSettings('rendering')}
                        {category === 'behaviours' && sceneSettings('behaviours')}
                        {category === 'logics' && sceneSettings('logics')}
                        {/* AR E VTO SONO PAGINE INTERE NOSTRE (utente, 2026-10-20), nello stile delle altre: gruppi, card e interruttori. */}
                        {category === 'ar' && (
                            <SettingsPage
                                title={T._('Augmented Reality', 'Admin')}
                                lead={stringFormat('{0} {1}', T._('Placement, real-world scale and conversion.', 'Admin'), savedWithProduct)}
                            >
                                <AREditor sceneEditor={sceneEditor} zkScene={zkScene} onSceneChanged={onSceneChanged} />
                            </SettingsPage>
                        )}
                        {category === 'vto' && (
                            <SettingsPage
                                title={T._('Virtual Try-On', 'Admin')}
                                lead={stringFormat('{0} {1}', T._('Type, options and reference files.', 'Admin'), savedWithProduct)}
                            >
                                <VTOEditor
                                    productModelID={zkProduct?.get?.('modelID')}
                                    additionalFeatures={additionalFeatures}
                                    onSceneChanged={onSceneChanged}
                                />
                            </SettingsPage>
                        )}
                    </SceneChanges>
                </Content>
            </DialogLayout>
        </ZTooltipLookContext.Provider>
    );
};

export default SceneEditorSettingsDialog;
